import { ReactNode, useEffect, useState } from 'react';
import moment from 'moment';
import { Navigate } from 'react-router-dom';
import Select from 'react-select';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import {
  ArrowCta,
  FormCard,
  FormControl,
  Input,
  Link,
  Text,
  VStack,
} from 'components/ui';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import useEligibilityRequest from '../../api/onboarding/mutations/useEligibilityRequest';
import { ApiError, HTTP_TOO_MANY_REQUESTS } from '../../hooks/useApi';
import { useUserEligibility } from '../../api/user/useUserEligibility';
import useUserRegisterPut from '../../api/user/mutations/useUserRegisterPut';
import { Affiliate } from '../../models/affiliate';
import CardInput from '../EligibilityView/components/CardInput';
import { RATE_LIMIT_MESSAGE } from '../EligibilityView/components/EligibilityError';
import { PlanHolderInfo } from '../EligibilityView/components/PlanHolderInfo';
import SignUpInfoAffiliateLookup from '../OnboardingForm/SignUpInfoAffiliateLookup';
import { SUBSCRIBED_HOME_PATH } from '../../utils/subscribedHome';
import { LoadingScreen } from '../ui/LoadingScreen';
import { AccountPage } from './AccountPage';
import { CurrentHealthPlan } from './CurrentHealthPlan';
import { useAccountUser } from './useAccountUser';

const RELATIONSHIP_OPTIONS = [
  { label: 'Self', value: 'self' },
  { label: 'Spouse', value: 'spouse' },
  { label: 'Child', value: 'child' },
  { label: 'Other', value: 'other' },
];

const DEFAULT_RELATIONSHIP = 'self';

const relationshipSelectStyles = {
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base) => ({
    ...base,
    width: '100%',
    borderRadius: '8px',
    height: 48,
    padding: '0px 8px',
    backgroundColor: 'rgba(4, 51, 56, 0.05)',
    borderColor: 'transparent',
    boxShadow: 'none',
  }),
};

const DATE_PATTERN = /(\d{4})-(\d{2})-(\d{2})/;

type HealthPlanForm = {
  firstName: string;
  lastName: string;
  memberId: string;
  groupId: string;
  relationship: string;
  subscriberMemberId: string;
  subscriberDob: string;
  subscriberGender: string;
  subscriberFirstName: string;
  subscriberLastName: string;
};

type FieldErrors = Partial<Record<keyof HealthPlanForm, string>>;

const EMPTY_FORM: HealthPlanForm = {
  firstName: '',
  lastName: '',
  memberId: '',
  groupId: '',
  relationship: DEFAULT_RELATIONSHIP,
  subscriberMemberId: '',
  subscriberDob: '',
  subscriberGender: '',
  subscriberFirstName: '',
  subscriberLastName: '',
};

const TextField = ({
  label,
  value,
  error,
  testId,
  onChange,
}: {
  label: ReactNode;
  value: string;
  error?: string;
  testId: string;
  onChange: (value: string) => void;
}) => (
  <FormControl required invalid={Boolean(error)}>
    <FormControl.Label>{label}</FormControl.Label>
    <Input
      data-testid={testId}
      maxWidth="100%"
      onChange={(event) => onChange(event.target.value)}
      type="text"
      value={value}
    />
    <FormControl.ErrorMessage mb={4}>
      <Text fontSize="md">{error}</Text>
    </FormControl.ErrorMessage>
  </FormControl>
);

const validate = (
  form: HealthPlanForm,
  affiliate: Affiliate | undefined
): FieldErrors => {
  const errors: FieldErrors = {};
  const collectPlanHolder = form.relationship !== DEFAULT_RELATIONSHIP;

  if (!form.firstName.trim()) errors.firstName = 'First name is required';
  if (!form.lastName.trim()) errors.lastName = 'Last name is required';
  if (!form.memberId.trim()) {
    errors.memberId = 'Member ID (or Contract number) is required';
  }
  if (affiliate?.group_id_required && !form.groupId.trim()) {
    errors.groupId = 'Group ID is required';
  }
  if (form.groupId.length > 32) {
    errors.groupId = 'Group ID must not be more than 32 characters';
  }

  if (collectPlanHolder) {
    if (!form.subscriberDob) {
      errors.subscriberDob = "Plan holder's date of birth is required";
    } else if (!form.subscriberDob.match(DATE_PATTERN)) {
      errors.subscriberDob =
        "Plan holder's date of birth must be in YYYY-MM-DD format";
    } else if (moment().diff(form.subscriberDob, 'years') < 18) {
      errors.subscriberDob = 'Plan holder must be 18+ in order to use Ayble';
    }

    if (!form.subscriberMemberId.trim()) {
      errors.subscriberMemberId = "Plan holder's member ID is required";
    }
    if (!form.subscriberFirstName.trim()) {
      errors.subscriberFirstName = "Plan holder's first name is required";
    }
    if (!form.subscriberLastName.trim()) {
      errors.subscriberLastName = "Plan holder's last name is required";
    }
    if (!form.subscriberGender) {
      errors.subscriberGender = "Plan holder's gender is required";
    }
  }

  return errors;
};

type ScannedCard = {
  details?: {
    memberNumber?: { value?: string };
    groupNumber?: { value?: string };
    memberName?: { value?: string };
  };
};

export const AccountHealthPlan = () => {
  const { user, isLoading: userLoading } = useAccountUser();
  const {
    eligibility,
    isLoading: eligibilityLoading,
    isError: eligibilityError,
  } = useUserEligibility();
  const { affiliate: currentAffiliate, isLoading: affiliateLoading } =
    useAffiliate({
      affiliateId: eligibility?.affiliate,
    });
  const [planName, setPlanName] = useState('');
  const [planSource, setPlanSource] = useState<string | null>(null);
  const [planTouched, setPlanTouched] = useState(false);
  const { affiliate: pickedAffiliate, isLoading: pickedAffiliateLoading } =
    useAffiliate({
      affiliateId: planTouched ? planSource : null,
    });
  const { mutation } = useEligibilityRequest();
  const { mutation: updateRegistration } = useUserRegisterPut();
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const [inputView, setInputView] = useState<'scan' | 'upload' | null>(null);
  const [form, setForm] = useState<HealthPlanForm>(EMPTY_FORM);
  const [seeded, setSeeded] = useState(false);
  const [formHasChanged, setFormHasChanged] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saveError, setSaveError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (seeded || userLoading || eligibilityLoading || !user) return;
    if (!eligibility && !eligibilityError) return;

    const patient = eligibility?.patient;

    setForm({
      ...EMPTY_FORM,
      firstName: patient?.first_name || user.first_name || '',
      lastName: patient?.last_name || user.last_name || '',
    });
    setSeeded(true);
  }, [
    eligibility,
    eligibilityError,
    eligibilityLoading,
    seeded,
    user,
    userLoading,
  ]);

  const clearErrors = (...fields: (keyof HealthPlanForm)[]) => {
    setErrors((current) => {
      if (!fields.some((field) => current[field])) return current;
      const next = { ...current };
      fields.forEach((field) => delete next[field]);
      return next;
    });
  };

  const setField = (field: keyof HealthPlanForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormHasChanged(true);
    clearErrors(field);
  };

  const populateCardData = (data: ScannedCard) => {
    const capitalize = (value: string) =>
      value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    const memberId = data.details?.memberNumber?.value;
    const groupId = data.details?.groupNumber?.value;
    const memberName = data.details?.memberName?.value?.split(' ') ?? [];

    setForm((current) => ({
      ...current,
      ...(memberId ? { memberId } : {}),
      ...(groupId ? { groupId } : {}),
      ...(memberName.length
        ? {
            firstName: capitalize(memberName[0]),
            lastName: capitalize(memberName[memberName.length - 1]),
          }
        : {}),
    }));
    setFormHasChanged(true);
  };

  if (userLoading || eligibilityLoading || !user) {
    return <LoadingScreen />;
  }

  if (!eligibility && !eligibilityError) {
    return <Navigate replace to={SUBSCRIBED_HOME_PATH} />;
  }

  if (!seeded || (!!eligibility?.affiliate && affiliateLoading)) {
    return <LoadingScreen />;
  }

  if (submitted) {
    return (
      <AccountPage
        showBack
        testID="account-health-plan"
        title="Update your health plan"
      >
        <Box bg="feather.200" px="15px" py="14px" role="status" rounded="16px">
          <Text>Thank you for updating your information.</Text>
        </Box>
      </AccountPage>
    );
  }

  const collectPlanHolder = form.relationship !== DEFAULT_RELATIONSHIP;
  const usingPickedPlan =
    planTouched && !!planSource && !pickedAffiliateLoading;
  const affiliate = usingPickedPlan ? pickedAffiliate : currentAffiliate;
  // useAffiliate does not throw, so a failed lookup comes back as no affiliate.
  // Keep the card fields visible in that case, or a required group ID is hidden.
  const affiliateUnavailable = usingPickedPlan
    ? !pickedAffiliate
    : !!eligibility?.affiliate && !affiliateLoading && !currentAffiliate;
  const showGroupId =
    affiliateUnavailable ||
    !!affiliate?.candid_eligibility ||
    !!affiliate?.group_id_required;
  const planHolderError = [
    errors.subscriberMemberId,
    errors.subscriberFirstName,
    errors.subscriberLastName,
    errors.subscriberDob,
    errors.subscriberGender,
  ].find(Boolean);
  const requiredFieldsFilled =
    !!planSource &&
    !!form.memberId &&
    (!affiliate?.group_id_required || !!form.groupId) &&
    (!collectPlanHolder ||
      (!!form.subscriberMemberId &&
        !!form.subscriberFirstName &&
        !!form.subscriberLastName &&
        !!form.subscriberDob &&
        !!form.subscriberGender));
  const isScanningOnMobile = inputView === 'scan' && !!isMobile;

  const choosePlan = (picked: { name: string; utm_source: string }) => {
    setPlanName(picked.name);
    setPlanSource(picked.utm_source);
    setPlanTouched(true);
    setFormHasChanged(true);
  };

  const onSubmit = async () => {
    const validationErrors = validate(form, affiliate);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSaveError('');
    const groupId = form.groupId.trim();

    if (planSource && planSource !== currentAffiliate?.utm_source) {
      const updated = await updateRegistration.mutateAsync({
        affiliate: planSource,
      });
      if (!updated) {
        setSaveError("We couldn't update your health plan. Please try again.");
        return;
      }
    }

    // A denial still confirms here. Only a failed request stays on the form.
    // Either way, a subscribed member is not sent back through signup.
    try {
      await mutation.mutateAsync({
        user_id: user.id,
        member_id: form.memberId.replace(/ /g, ''),
        user_email: user.email,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        ...(groupId ? { group_id: groupId } : {}),
        relationship_to_subscriber: form.relationship || DEFAULT_RELATIONSHIP,
        ...(collectPlanHolder && {
          subscriber: {
            member_id: form.subscriberMemberId.replace(/ /g, ''),
            date_of_birth: form.subscriberDob,
            gender: form.subscriberGender,
            first_name: form.subscriberFirstName.trim(),
            last_name: form.subscriberLastName.trim(),
          },
        }),
      });
      setSubmitted(true);
    } catch (error) {
      setSaveError(
        error instanceof ApiError && error.status === HTTP_TOO_MANY_REQUESTS
          ? RATE_LIMIT_MESSAGE
          : "We couldn't send your update. Please try again."
      );
    }
  };

  return (
    <AccountPage
      actionArea={
        isScanningOnMobile ? undefined : (
          <ArrowCta
            disabled={
              updateRegistration.isLoading ||
              mutation.isLoading ||
              !formHasChanged ||
              !requiredFieldsFilled
            }
            loading={mutation.isLoading}
            onClick={onSubmit}
          >
            Save
          </ArrowCta>
        )
      }
      intro={
        <>
          The following is the health plan information we currently have on
          file for you.{' '}
          <strong>If your health plan information has changed</strong>, please
          submit the form below, entering the details exactly as they appear on
          your insurance card.
        </>
      }
      showBack={!isScanningOnMobile}
      testID="account-health-plan"
      title="Update your health plan"
    >
      <FormCard>
        <Text textStyle="label.section">Current health plan</Text>
        {eligibility && !eligibilityError ? (
          <CurrentHealthPlan
            eligibility={eligibility}
            planName={currentAffiliate?.name}
          />
        ) : (
          <Text color="blueGray.600">
            We couldn't load your health plan. You can still send an update.
          </Text>
        )}
      </FormCard>

      <Box
        bgColor="feather.200"
        pb="15px"
        position="relative"
        pt="14px"
        px="15px"
        rounded="16px"
        w="100%"
      >
        <CardInput
          onInputViewChange={setInputView}
          onScanSuccess={populateCardData}
          userId={user.id}
        />
      </Box>

      {!isScanningOnMobile && (
        <VStack alignItems="stretch" gap="18px" w="100%">
          <FormCard w="100%">
            <SignUpInfoAffiliateLookup
              affiliateName={planName}
              onAffilSelection={choosePlan}
              onClear={() => {
                setPlanName('');
                setPlanSource(null);
                setPlanTouched(true);
                setFormHasChanged(true);
              }}
            />
            <TextField
              error={errors.firstName}
              label="First Name (exactly as appears on card)"
              onChange={(value) => setField('firstName', value)}
              testId="account-card-first-name"
              value={form.firstName}
            />
            <TextField
              error={errors.lastName}
              label="Last Name (exactly as appears on card)"
              onChange={(value) => setField('lastName', value)}
              testId="account-card-last-name"
              value={form.lastName}
            />

            <TextField
              error={errors.memberId}
              label="Member ID (or Contract number)"
              onChange={(value) => setField('memberId', value)}
              testId="account-member-id"
              value={form.memberId}
            />

            {showGroupId && (
              <TextField
                error={errors.groupId}
                label={
                  affiliate?.group_id_required
                    ? 'Group ID'
                    : 'Group ID (Required if your employer covers Ayble)'
                }
                onChange={(value) => setField('groupId', value)}
                testId="account-group-id"
                value={form.groupId}
              />
            )}

            <FormControl required>
              <FormControl.Label>
                What is your relationship to the subscriber (insured)?
              </FormControl.Label>
              <Select
                aria-label="Choose Relationship with subscriber"
                components={{ IndicatorSeparator: () => null }}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onChange={(selection) => {
                  if (selection) setField('relationship', selection.value);
                }}
                options={RELATIONSHIP_OPTIONS}
                styles={relationshipSelectStyles}
                value={RELATIONSHIP_OPTIONS.find(
                  (option) => option.value === form.relationship
                )}
              />
            </FormControl>

            {collectPlanHolder && (
              <Box w="100%">
                <FormControl required invalid={Boolean(planHolderError)}>
                  <PlanHolderInfo
                    dob={form.subscriberDob}
                    firstName={form.subscriberFirstName}
                    gender={form.subscriberGender}
                    lastName={form.subscriberLastName}
                    memberId={form.subscriberMemberId}
                    onPlanHolderInfoChange={(info) => {
                      setForm((current) => ({
                        ...current,
                        subscriberMemberId: info.memberId,
                        subscriberDob: info.dob,
                        subscriberGender: info.gender,
                        subscriberFirstName: info.firstName,
                        subscriberLastName: info.lastName,
                      }));
                      setFormHasChanged(true);
                      clearErrors(
                        'subscriberMemberId',
                        'subscriberDob',
                        'subscriberGender',
                        'subscriberFirstName',
                        'subscriberLastName'
                      );
                    }}
                  />
                  <FormControl.ErrorMessage>
                    <Text fontSize="md">{planHolderError}</Text>
                  </FormControl.ErrorMessage>
                </FormControl>
              </Box>
            )}
          </FormCard>

          {saveError && (
            <Text color="red.500" fontSize="md">
              {saveError}
            </Text>
          )}
          {Object.keys(errors).length > 0 && (
            <Text color="red.500" fontSize="md">
              Need help? Contact{' '}
              <Link
                _text={{ textTransform: 'none' }}
                href="mailto:support@ayblehealth.com"
              >
                support@ayblehealth.com
              </Link>
              .
            </Text>
          )}
        </VStack>
      )}
    </AccountPage>
  );
};
